import React from "react"
import type { TFunction } from "i18next"
import type { FormDescriptionImage } from "@f2e/shared/types"
import { FieldDescription } from "@f2e/ui/components/InteractionRequestForm"

import { Label, Textarea } from "@f2e/ui/components/base"

import { BaseFieldEditor, BaseFieldProps } from "./BaseFieldEditor"

export type LongTextField = BaseFieldProps<{
  type: "longText"
  name: string
  question: string
  label: string
  placeholder: string
  required: boolean
}>

export const LongTextFieldEditor: React.FC<LongTextField> = ({
  field,
  onChange,
  disabled,
}) => {
  return (
    <BaseFieldEditor field={field} onChange={onChange} disabled={disabled} />
  )
}

export default LongTextFieldEditor

export const LongTextFieldRenderer: React.FC<{
  field: {
    question: string
    placeholder: string
    required: boolean
    helpText?: string
    descriptionImage?: FormDescriptionImage
  }
  mode: "preview" | "runtime"
  value?: string
  onChange?: (v: string) => void
  disabled?: boolean
  error?: string
}> = ({ field, mode, value, onChange, disabled, error }) => {
  const isPreview = mode === "preview"
  const [local, setLocal] = React.useState<string>("")
  const val = isPreview ? local : (value ?? "")
  const setVal = (v: string) => {
    if (isPreview) setLocal(v)
    else onChange?.(v)
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs">
        {field.question}{" "}
        {field.required ? <span className="text-red-500">*</span> : null}
      </Label>
      <FieldDescription
        helpText={field.helpText}
        descriptionImage={field.descriptionImage}
      />
      <Textarea
        rows={2}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        placeholder={field.placeholder}
        disabled={disabled}
        className={
          error ? "border-red-500 focus-visible:ring-red-500" : undefined
        }
      />
      {error ? <div className="text-xs text-red-500">{error}</div> : null}
    </div>
  )
}

export function validateLongTextField(
  field: { name: string; required?: boolean },
  value: unknown,
  t: TFunction
): string | undefined {
  if (!field.required) return undefined
  const str = value === undefined || value === null ? "" : String(value)
  if (str.trim() === "")
    return t("workspace.agent.basicForm.formBuilder.validation.required")
  return undefined
}
